import { useMemo, useState } from 'react';
import { formatDate } from '@/lib/utils';

export interface ChartPoint {
  value: number;
  measuredOn: string;
}

export interface ChartMarker {
  date: string;
  label: string;
}

// Lightweight inline SVG line chart (no charting library). The x-axis is the
// actual timeline (positions by date), so external markers (e.g. bespoke dates)
// line up correctly. Y-axis is the size scale; hover shows date + size.
export function MeasurementChart({
  points,
  unit,
  markers = [],
}: {
  points: ChartPoint[];
  unit: string;
  markers?: ChartMarker[];
}) {
  const PLOT_H = 96; // px
  const [hover, setHover] = useState<number | null>(null);

  const { coords, polyline, min, max, mid, xForDate, tMin, tMax } = useMemo(() => {
    const values = points.map((p) => p.value);
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const span = hi - lo;
    const n = points.length;

    const times = points.map((p) => new Date(p.measuredOn).getTime());
    const loT = Math.min(...times);
    const hiT = Math.max(...times);
    const tSpan = hiT - loT;

    // x maps a timestamp into the 5..95 range; falls back to even spacing when
    // all readings share a date.
    const xForTime = (t: number) =>
      tSpan === 0 ? 50 : 5 + ((t - loT) / tSpan) * 90;

    const cs = points.map((p, i) => {
      const x = tSpan === 0 ? (n === 1 ? 50 : 5 + (i / (n - 1)) * 90) : xForTime(times[i]);
      const y = span === 0 ? 50 : 10 + (1 - (p.value - lo) / span) * 80;
      return { x, y };
    });

    const round = (v: number) => Number(v.toFixed(1));
    return {
      coords: cs,
      polyline: cs.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' '),
      min: round(lo),
      max: round(hi),
      mid: round((lo + hi) / 2),
      xForDate: (iso: string) => xForTime(new Date(iso).getTime()),
      tMin: loT,
      tMax: hiT,
    };
  }, [points]);

  // Markers that fall within the charted date range.
  const visibleMarkers = markers
    .map((m) => ({ ...m, t: new Date(m.date).getTime() }))
    .filter((m) => !Number.isNaN(m.t) && m.t >= tMin && m.t <= tMax)
    .map((m) => ({ ...m, x: xForDate(m.date) }));

  const shortDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });

  return (
    <div className="mt-3">
      <div className="flex gap-2">
        {/* Y-axis: size scale */}
        <div
          className="flex flex-col justify-between items-end text-[9px] text-muted-foreground self-start flex-shrink-0"
          style={{ height: PLOT_H }}
        >
          <span>{max}{unit}</span>
          {min !== max && <span>{mid}{unit}</span>}
          <span>{min}{unit}</span>
        </div>

        {/* Plot + x-axis */}
        <div className="flex-1 min-w-0">
          <div className="relative" style={{ height: PLOT_H }}>
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 w-full h-full"
              role="img"
              aria-label="Measurement history chart"
            >
              {/* Bespoke / event markers (vertical lines at their date). */}
              {visibleMarkers.map((m, i) => (
                <line
                  key={`mk-${i}`}
                  x1={m.x}
                  x2={m.x}
                  y1={4}
                  y2={96}
                  stroke="currentColor"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                  className="text-accent-foreground/50"
                />
              ))}
              <polyline
                points={polyline}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                className="text-primary"
              />
            </svg>

            {/* Marker labels */}
            {visibleMarkers.map((m, i) => (
              <span
                key={`mkl-${i}`}
                className="absolute top-0 text-[8px] font-medium text-accent-foreground/80 whitespace-nowrap pointer-events-none"
                style={{ left: `${m.x}%`, transform: 'translate(-50%, 0)' }}
                title={`${m.label} · ${formatDate(m.date)}`}
              >
                {m.label}
              </span>
            ))}

            {/* Interactive dots (HTML, positioned by %) */}
            {coords.map((c, i) => (
              <button
                key={i}
                type="button"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h) => (h === i ? null : h))}
                onClick={() => setHover((h) => (h === i ? null : i))}
                className="absolute"
                style={{ left: `${c.x}%`, top: `${c.y}%`, transform: 'translate(-50%, -50%)' }}
                aria-label={`${points[i].value} ${unit} on ${formatDate(points[i].measuredOn)}`}
              >
                <span
                  className={`block rounded-full bg-primary ring-2 ring-background transition-all ${
                    hover === i ? 'w-3 h-3' : 'w-2 h-2'
                  }`}
                />
              </button>
            ))}

            {/* Tooltip */}
            {hover !== null && (
              <div
                className="absolute z-10 px-2 py-1 rounded-lg bg-foreground text-background text-[10px] font-medium whitespace-nowrap pointer-events-none shadow-md"
                style={{
                  left: `${coords[hover].x}%`,
                  top: `${coords[hover].y}%`,
                  transform: `translate(-50%, ${coords[hover].y < 30 ? '12px' : 'calc(-100% - 10px)'})`,
                }}
              >
                {points[hover].value} {unit} · {formatDate(points[hover].measuredOn)}
              </div>
            )}
          </div>

          {/* X-axis: dates */}
          <div className="relative h-4 mt-1">
            {coords.map((c, i) => (
              <span
                key={i}
                className="absolute text-[9px] text-muted-foreground whitespace-nowrap"
                style={{ left: `${c.x}%`, transform: 'translate(-50%, 0)' }}
              >
                {shortDate(points[i].measuredOn)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
